import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create the context
const LangflowContext = createContext();

// Custom hook to use the Langflow context
export const useLangflow = () => {
  const context = useContext(LangflowContext);
  if (!context) {
    throw new Error('useLangflow must be used within a LangflowProvider');
  }
  return context;
};

// Langflow Provider component
export const LangflowProvider = ({ children, authToken, flowId, userId, sessionId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(userId);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId || `session-${crypto.randomUUID()}`);

  useEffect(() => {
    if (sessionId) {
      setCurrentSessionId(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (userId) {
      setCurrentUserId(userId);
    }
  }, [userId]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Add a message to the conversation
  const addMessage = useCallback(message => {
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...message
      }
    ]);
  }, []);

  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Stream a message via the frn-platform-backend Wellness Buddy proxy, which
  // relays Langflow's AG-UI SSE event stream. The Langflow API key stays
  // server-side; the browser never talks to Langflow directly.
  const streamMessage = useCallback(
    async (messageText, onChunk, options = {}) => {
      if (!flowId) {
        throw new Error('Flow ID is required to stream messages');
      }

      setIsLoading(true);
      setError(null);

      // Add user message immediately
      const userMessage = {
        type: 'user',
        content: messageText,
        sender: 'user'
      };
      addMessage(userMessage);

      // Create assistant message placeholder
      const assistantMessageId = Date.now() + Math.random();
      const assistantMessage = {
        id: assistantMessageId,
        type: 'assistant',
        content: '',
        sender: 'assistant',
        streaming: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      let assistantContent = '';

      const appendContent = delta => {
        assistantContent += delta;
        setMessages(prev => prev.map(msg => (msg.id === assistantMessageId ? { ...msg, content: assistantContent } : msg)));
      };

      const appendStatus = statusText => {
        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date().toISOString(),
            type: 'status',
            content: statusText,
            sender: 'system'
          }
        ]);
      };

      try {
        const response = await fetch(`${process.env.ROOT_URL}/wellnessBuddy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            flow_id: flowId,
            input_value: messageText,
            session_id: currentSessionId,
            ...options.additionalParams
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let runError = null;
        let partialChunk = '';
        const decoder = new TextDecoder();
        const reader = response.body.getReader();

        while (true) {
          const { done, value } = await reader.read();

          const txt = partialChunk + decoder.decode(value || new Uint8Array(), { stream: !done });
          const lines = txt.split('\n');
          partialChunk = done ? '' : lines.pop();

          lines
            .filter(line => line.startsWith('data: '))
            .forEach(line => {
              const event = JSON.parse(line.slice('data: '.length));
              switch (event.type) {
                case 'TEXT_MESSAGE_CONTENT': {
                  const delta = event.delta ?? '';
                  appendContent(delta);
                  if (onChunk) onChunk(delta, assistantContent);
                  break;
                }
                case 'TOOL_CALL_START':
                  appendStatus('Using a tool…');
                  break;
                case 'RUN_ERROR':
                  runError = event.message || 'Langflow run error';
                  break;
                default:
                  break;
              }
            });

          if (done) break;
        }

        if (runError) {
          throw new Error(runError);
        }

        if (!assistantContent.trim()) {
          assistantContent = 'No response received from the server.';
        }

        // Final update to mark streaming as complete
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId ? { ...msg, content: assistantContent, streaming: false } : msg
          )
        );

        return { userMessage, assistantMessage: { ...assistantMessage, content: assistantContent, streaming: false } };
      } catch (err) {
        const errorMessage = err.message || 'Failed to stream message';
        setError(errorMessage);

        // Remove the now-orphaned streaming placeholder and surface an error message instead
        setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));

        const errorChatMessage = {
          type: 'error',
          content: `Error: ${errorMessage}`,
          sender: 'system'
        };
        addMessage(errorChatMessage);

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authToken, flowId, addMessage, currentSessionId]
  );

  const value = {
    // State
    isLoading,
    error,
    messages,

    // Configuration
    flowId,
    currentSessionId,

    // Actions
    streamMessage,
    addMessage,
    clearMessages,
    clearError,
    setCurrentUserId
  };

  return <LangflowContext.Provider value={value}>{children}</LangflowContext.Provider>;
};

export default LangflowProvider;
